"use client";

import { useState } from "react";
import { legalNoticeData } from "@/data/legal-notice";
import { Icon } from "@iconify/react";
import { BreadcrumbHomepage } from "@/components/sections/BreadcrumbHomepage";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

export function MentionsLegalesContent() {
  const t = useTranslations("LegalNotice");
  const [openArticles, setOpenArticles] = useState<number[]>(
    Array.from({ length: legalNoticeData.length }, (_, index) => index),
  );

  const toggleArticle = (index: number) => {
    setOpenArticles((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  // Variants pour les animations cohérentes
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const renderContent = (descriptionKey: string) => {
    return (
      <div className="text-md font-inter flex flex-col gap-4 xl:gap-8">
        <div>
          <p className="text-accent">{t(`${descriptionKey}`)}</p>
        </div>
      </div>
    );
  };

  return (
    <section className="min-h-[85dvh] px-4 pt-[8rem] ph:px-24 md:px-48 lg:px-72 xl:px-96">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <BreadcrumbHomepage />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
        className="w-full py-20"
      >
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          className="mb-8 text-3xl font-bold text-black"
        >
          {t("title")}
        </motion.h1>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-4 text-black"
        >
          {legalNoticeData.map((article, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="border-b border-b-black"
            >
              <button
                onClick={() => toggleArticle(index)}
                className="flex w-full items-center justify-between text-left"
              >
                <h2 className="text-xl font-semibold">{t(article.titleKey)}</h2>
                <motion.div
                  animate={{ rotate: openArticles.includes(index) ? 0 : 90 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Icon icon="heroicons:chevron-down" width={20} />
                </motion.div>
              </button>

              <motion.div
                animate={{
                  height: openArticles.includes(index) ? "auto" : 0,
                  marginBottom: openArticles.includes(index) ? "1.5rem" : 0,
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="overflow-hidden"
              >
                {openArticles.includes(index) &&
                  renderContent(article.descriptionKey)}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
